import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AccessibleIcon } from "radix-ui";
import {
	UserIcon,
	UsersIcon,
	GearIcon,
	PrinterIcon,
	FoldersIcon,
	MagnifyingGlassIcon,
} from "@phosphor-icons/react";
import Navigation from "@pinatagrams/fiesta/patterns/navigation";
import Indicator from "@pinatagrams/fiesta/components/indicator";
import { Button } from "@pinatagrams/fiesta/components/button";
import { Dialog } from "@pinatagrams/fiesta/components/dialog";
import TextInput from "@pinatagrams/fiesta/components/input";
import { Stack } from "@pinatagrams/fiesta/components/layout";
import { Body, Supplemental, Title } from "@pinatagrams/fiesta/components/text";
import { useFeedbackEvent } from "@pinatagrams/fiesta";

import { useAuthStore } from "#/stores/auth";
import { useRealtimeStore } from "#/stores/realtime-store";
import { useCollections } from "#/features/collections";
import { useOrderSearch } from "#/features/orders";
import { canManageUsers, canViewWorkflows } from "#/server/utils/auth/roles";
import { apiFetch } from "#/utils/api";

/**
 * Dashboard navigation component
 */
export const DashboardNavigation = () => {
	const location = useLocation();
	const user = useAuthStore((state) => state.user);
	const realtimeStatus = useRealtimeStore((state) => state.status);
	const { emit } = useFeedbackEvent();
	const navigate = useNavigate();
	const {
		data: collections,
		isLoading: collectionsLoading,
		refetch: refreshCollections,
	} = useCollections({ lifecycle: "rolling" });

	const rollingCollection = collections?.[0];
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [isOrderSearchOpen, setIsOrderSearchOpen] = useState(false);
	const [orderSearchTerm, setOrderSearchTerm] = useState("");
	const [debouncedOrderSearchTerm, setDebouncedOrderSearchTerm] = useState("");

	useEffect(() => {
		const timeout = setTimeout(() => {
			setDebouncedOrderSearchTerm(orderSearchTerm.trim());
		}, 250);

		return () => clearTimeout(timeout);
	}, [orderSearchTerm]);

	const {
		data: orderResults = [],
		isFetching: isSearchingOrders,
		error: orderSearchError,
	} = useOrderSearch(
		debouncedOrderSearchTerm,
		isOrderSearchOpen && debouncedOrderSearchTerm.length >= 2,
	);

	const refreshRollingCollection = useCallback(
		async (offsetBusinessDays?: number): Promise<void> => {
			if (isRefreshing) return;
			setIsRefreshing(true);
			try {
				const payload = typeof offsetBusinessDays === "number" ? { offsetBusinessDays } : undefined;
				const response = await apiFetch(`/collections/shopify/start`, {
					method: "POST",
					headers: payload ? { "Content-Type": "application/json" } : undefined,
					body: payload ? JSON.stringify(payload) : undefined,
				});
				if (!response.ok) {
					throw new Error(`Failed to refresh collection: ${response.statusText}`);
				}
				void refreshCollections();
			} catch (error) {
				emit({
					origin: { id: "dashboard-navigation" },
					impact: "blocking",
					severity: "fatal",
					title: "Collection Refresh Failed",
					message:
						error instanceof Error
							? error.message
							: "An unknown error occurred while refreshing the collection.",
				});
			} finally {
				setIsRefreshing(false);
			}
		},
		[emit, isRefreshing, refreshCollections],
	);

	const refreshRollingCollectionForTomorrow = useCallback(() => {
		void refreshRollingCollection(1);
	}, [refreshRollingCollection]);

	const canManageUsersFlag = canManageUsers(user?.roles || []);
	const canViewWorkflowsFlag = canViewWorkflows(user?.roles || []);
	const renderTabContent = (label: string, icon?: React.ReactNode) => (
		<>
			{icon && <AccessibleIcon.Root label={label}>{icon}</AccessibleIcon.Root>}
			<span>{label}</span>
		</>
	);

	/**
	 * Determines current tab and whether we're on a detail route
	 */
	const getCurrentRoute = () => {
		const { pathname } = location;

		if (pathname.startsWith("/collection/") && pathname.endsWith("/print")) {
			return { tab: "print", isDetail: true };
		}

		if (pathname.startsWith("/collection/")) return { tab: "collections", isDetail: true };
		if (pathname === "/collections") return { tab: "collections", isDetail: false };

		if (pathname.startsWith("/workflow/")) return { tab: "workflows", isDetail: true };
		if (pathname === "/workflows") return { tab: "workflows", isDetail: false };

		if (pathname.startsWith("/user/")) return { tab: "users", isDetail: true };
		if (pathname === "/users") return { tab: "users", isDetail: false };

		if (pathname.startsWith("/profile/")) return { tab: "profile", isDetail: true };
		if (pathname === "/profile") return { tab: "profile", isDetail: false };

		return { tab: undefined, isDetail: false };
	};

	const { tab: currentTab } = getCurrentRoute();

	/**
	 * Determines the collection indicator status
	 */
	const getCollectionIndicator = () => {
		const props: { label: string; color: "unavailable" | "latest" | "stale" | "inprogress" } = {
			label: "No collection available",
			color: "unavailable",
		};

		if (!rollingCollection || !rollingCollection.fulfillAt) {
			return props;
		}

		const now = new Date();
		const fulfillAtDate = new Date(rollingCollection.fulfillAt);
		const isFulfillAtToday =
			fulfillAtDate.getFullYear() === now.getFullYear() &&
			fulfillAtDate.getMonth() === now.getMonth() &&
			fulfillAtDate.getDate() === now.getDate();
		const isFulfillAtPast = fulfillAtDate < now && !isFulfillAtToday;

		props.label = `Collection: ${rollingCollection.orders || 0} orders`;

		if (isFulfillAtPast) {
			props.color = "stale";
			props.label += " (overdue)";
		} else if (isFulfillAtToday) {
			props.color = "latest";
			props.label += " (due today)";
		} else {
			props.color = "latest";
			const daysUntil = Math.ceil(
				(fulfillAtDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
			);
			props.label += ` (due in ${daysUntil} days)`;
		}

		return props;
	};

	const indicatorStatus = getCollectionIndicator();

	const collectionsIcon = <FoldersIcon />;
	const printIcon = <PrinterIcon />;
	const workflowsIcon = <GearIcon />;
	const usersIcon = <UsersIcon />;
	const profileIcon = <UserIcon />;
	const toRouteOrderId = (orderId: string): string =>
		orderId.replace(/^gid:\/\/shopify\/Order\//, "");

	const handleSelectOrder = useCallback(
		(orderId: string) => {
			setIsOrderSearchOpen(false);
			setOrderSearchTerm("");
			setDebouncedOrderSearchTerm("");
			void navigate(`/order/${encodeURIComponent(toRouteOrderId(orderId))}`);
		},
		[navigate],
	);

	return (
		<Navigation value={currentTab}>
			<Navigation.Title>
				<Title hierarchy="subtitle">Paper</Title>
			</Navigation.Title>

			<Navigation.List>
				<Navigation.Tab value="collections" icon={collectionsIcon} asChild>
					<Link to="/collections">{renderTabContent("Collections", collectionsIcon)}</Link>
				</Navigation.Tab>

				{location.pathname.startsWith("/collection/") && location.pathname.endsWith("/print") && (
					<Navigation.Tab value="print" icon={printIcon} variant="subroute" asChild>
						<Link to={location.pathname}>{renderTabContent("Print", printIcon)}</Link>
					</Navigation.Tab>
				)}

				{canViewWorkflowsFlag && (
					<Navigation.Tab value="workflows" icon={workflowsIcon} asChild>
						<Link to="/workflows">{renderTabContent("Workflows", workflowsIcon)}</Link>
					</Navigation.Tab>
				)}

				{canManageUsersFlag && (
					<Navigation.Tab value="users" icon={usersIcon} asChild>
						<Link to="/users">{renderTabContent("Users", usersIcon)}</Link>
					</Navigation.Tab>
				)}

				<Navigation.Tab value="profile" icon={profileIcon} asChild>
					<Link to="/profile">{renderTabContent("Profile", profileIcon)}</Link>
				</Navigation.Tab>
			</Navigation.List>

			<Navigation.Footer>
				<Button
					size="sm"
					emphasis="subtle"
					onClick={() => setIsOrderSearchOpen(true)}
					icons={[MagnifyingGlassIcon]}
				>
					Search Orders
				</Button>

				{realtimeStatus !== "connected" && (
					<Title
						hierarchy="subtitle"
						emphasis="muted"
						style={{ fontSize: "0.875rem", marginLeft: "auto" }}
					>
						{realtimeStatus}
					</Title>
				)}
				<Indicator
					ariaLabel={indicatorStatus.label}
					status={indicatorStatus.color}
					overlay={true}
					dropdown={
						<Stack gap="xs">
							<Button
								onClick={() => void refreshRollingCollection()}
								disabled={collectionsLoading || isRefreshing}
								size="sm"
							>
								{"Refresh Collection"}
							</Button>
							<Button
								onClick={refreshRollingCollectionForTomorrow}
								disabled={collectionsLoading || isRefreshing}
								size="sm"
							>
								{"Refresh For Tomorrow"}
							</Button>
						</Stack>
					}
				/>
			</Navigation.Footer>

			<Dialog open={isOrderSearchOpen} onOpenChange={setIsOrderSearchOpen}>
				<Dialog.Content>
					<Dialog.Title>Order Search</Dialog.Title>
					<Dialog.Body>
						<Stack gap="xs">
							<TextInput
								placeholder="Search orders by customer, name, or Shopify query"
								value={orderSearchTerm}
								onChange={(event) => setOrderSearchTerm(event.target.value)}
							/>

							<Supplemental>
								Type at least 2 characters. You can also use Shopify syntax (for example:
								financial_status:paid).
							</Supplemental>

							{debouncedOrderSearchTerm.length >= 2 && isSearchingOrders && (
								<Body emphasis="muted">Searching…</Body>
							)}

							{orderSearchError && <Body>{orderSearchError.message}</Body>}

							{debouncedOrderSearchTerm.length >= 2 &&
								!isSearchingOrders &&
								!orderSearchError &&
								orderResults.length === 0 && <Body emphasis="muted">No orders found.</Body>}

							{orderResults.map((order) => (
								<Button
									key={order.id}
									size="sm"
									emphasis="ghost"
									onClick={() => handleSelectOrder(order.id)}
								>
									<Stack gap="none">
										<Body>{order.name}</Body>
										<Supplemental>
											{order.customer?.displayName || order.customer?.email || "Unknown customer"}
										</Supplemental>
									</Stack>
								</Button>
							))}
						</Stack>
					</Dialog.Body>
				</Dialog.Content>
			</Dialog>
		</Navigation>
	);
};
